import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { ArrowRight, Briefcase, MapPin } from 'lucide-react';

import { loadRelatedJobs } from 'redux/modules/jobs';
import JobCard from 'components/JobCard/JobCard';
import Loader from '../Loader/Loader';
import {
  beginsWithVowel,
  toSeoUrl,
  toPlural,
  seoCountryNameToCountyCode,
} from 'helpers/HelperFunctions';
import toSentenceCase, { pluralDisplayRole } from 'helpers/sentenceCase';

import styles from './RelatedJobs.module.scss';

const RelatedJobs = ({
  briefId,
  role,
  skill,
  user,
  country,
  city,
  locationWithRoleLink,
  remote = false,
}) => {
  const dispatch = useDispatch();
  const jobsState = useSelector((state) => state.jobs);

  const place = city || country;
  const showPlace = !remote && Boolean(place);
  const locationWithoutRoleLink = city
    ? `/jobs/in/${toSeoUrl(country)}/${toSeoUrl(city)}`
    : `/jobs/in/${toSeoUrl(country)}`;

  useEffect(() => {
    dispatch(loadRelatedJobs(briefId, role, user, skill));
  }, []);

  const relatedJobsList = jobsState?.relatedJobs.map((rj) => {
    return <JobCard key={rj.id} brief={rj} relatedJobs />;
  });

  let findUrl = '/hire-a-freelancer';
  if (role && country && city) {
    findUrl = `/find/${toSeoUrl(toPlural(role))}/${seoCountryNameToCountyCode(
      country
    )}/${toSeoUrl(city)}`;
  } else if (role && country) {
    findUrl = `/find/${toSeoUrl(toPlural(role))}/${seoCountryNameToCountyCode(
      country
    )}`;
  } else if (role) {
    findUrl = `/find/${toSeoUrl(toPlural(role))}`;
  }

  const hasRole = Boolean(role);
  const roleLabel = toSentenceCase(role);
  const rolePlural = pluralDisplayRole(role);
  const roleJobsHref = hasRole ? `/jobs/${toSeoUrl(toPlural(role))}` : null;
  const hireRoleHref = hasRole ? `/find/${toSeoUrl(toPlural(role))}` : null;
  const showCityHireLink = hasRole && showPlace && findUrl !== hireRoleHref;

  const chips = [];
  if (showPlace && city) {
    if (hasRole) {
      chips.push({
        href: locationWithRoleLink,
        label: `${roleLabel} in ${city}`,
        icon: 'map',
      });
    }
    chips.push({
      href: locationWithoutRoleLink,
      label: city,
      icon: 'map',
    });
  } else if (showPlace) {
    if (hasRole) {
      chips.push({
        href: locationWithRoleLink,
        label: `${roleLabel} in ${country}`,
        icon: 'map',
      });
    }
    chips.push({
      href: locationWithoutRoleLink,
      label: country,
      icon: 'map',
    });
  }
  chips.push({
    href: '/jobs',
    label: 'All freelance jobs',
    icon: 'briefcase',
  });

  return (
    <div className={styles.RelatedJobs}>
      <>
        <h2 className={styles.RelatedJobs__title}>Similar Jobs</h2>
        {jobsState.loading && <Loader loading />}
        {relatedJobsList}
      </>
      <div className={styles.moreJobs}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.heading}>More jobs like this</h2>
            <p className={styles.sub}>
              Browse this role, and the pages clients use to hire.
            </p>
          </div>
          {hasRole && (
            <a className={styles.seeAll} href={roleJobsHref}>
              See all {roleLabel} jobs
              <ArrowRight size={16} aria-hidden="true" />
            </a>
          )}
        </div>
        <ul className={styles.chips}>
          {chips.map((chip) => (
            <li key={chip.href + chip.label}>
              <a className={styles.chip} href={chip.href}>
                {chip.icon === 'map' ? (
                  <MapPin
                    size={14}
                    aria-hidden="true"
                    className={styles.chipIcon}
                  />
                ) : (
                  <Briefcase
                    size={14}
                    aria-hidden="true"
                    className={styles.chipIcon}
                  />
                )}
                {chip.label}
              </a>
            </li>
          ))}
        </ul>
        {hasRole && (
          <p className={styles.clientLine}>
            Hiring {beginsWithVowel(roleLabel) ? 'an' : 'a'} {roleLabel}?{' '}
            <a href={hireRoleHref}>Browse {rolePlural}</a>
            {showCityHireLink && (
              <>
                <span className={styles.clientSep} aria-hidden="true">
                  ·
                </span>
                <a href={findUrl}>
                  {rolePlural} in {place}
                </a>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

RelatedJobs.propTypes = {
  briefId: PropTypes.number,
  role: PropTypes.string,
  skill: PropTypes.string,
  user: PropTypes.object,
  country: PropTypes.string,
  city: PropTypes.string,
  locationWithRoleLink: PropTypes.string,
  remote: PropTypes.bool,
};

export default RelatedJobs;
